import React from "react";
import { Card, Form, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const QuestionnaireDecoder = ({ content, readOnly = false, onChange, answers = {} }) => {
  const { t } = useTranslation();

  if (!Array.isArray(content) || content.length === 0) {
    return (
      <div className="text-muted text-center p-3">
        {t("questionnaires.noFields")}
      </div>
    );
  }

  const renderField = (field, index) => {
    const { type, label, options = [], min, max } = field;

    switch (type) {
      case "text":
        return (
          <Form.Group key={index} className="mb-3">
            <Form.Label>{label}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              readOnly={readOnly}
              value={answers[index] || ""}
              placeholder={readOnly ? "" : t("questionnaires.enterText")}
              onChange={(e) => onChange?.(index, e.target.value)}
            />
          </Form.Group>
        );

      case "single":
        return (
          <Form.Group key={index} className="mb-3">
            <Form.Label>{label}</Form.Label>
            {readOnly ? (
              <div className="p-2 border rounded bg-light">
                {options.map((option, idx) => (
                  <div key={idx} className="mb-1">
                    <Form.Check
                      type="radio"
                      label={option}
                      checked={answers[index] === option}
                      disabled
                      readOnly
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div>
                {options.map((option, idx) => (
                  <Form.Check
                    key={idx}
                    type="radio"
                    name={`field_${index}`}
                    label={option}
                    className="mb-1"
                    checked={answers[index] === option}
                    onChange={() => onChange?.(index, option)}
                  />
                ))}
              </div>
            )}
          </Form.Group>
        );

      case "multi":
        const selectedOptions = Array.isArray(answers[index]) ? answers[index] : [];
        return (
          <Form.Group key={index} className="mb-3">
            <Form.Label>{label}</Form.Label>
            {readOnly ? (
              <div className="p-2 border rounded bg-light">
                {options.map((option, idx) => (
                  <div key={idx} className="mb-1">
                    <Form.Check
                      type="checkbox"
                      label={option}
                      checked={selectedOptions.includes(option)}
                      disabled
                      readOnly
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div>
                {options.map((option, idx) => (
                  <Form.Check
                    key={idx}
                    type="checkbox"
                    name={`field_${index}_${idx}`}
                    label={option}
                    className="mb-1"
                    checked={selectedOptions.includes(option)}
                    onChange={(e) => onChange?.(index, { option, checked: e.target.checked })}
                  />
                ))}
              </div>
            )}
          </Form.Group>
        );

      case "scale":
        const scaleValue = answers[index] !== undefined ? answers[index] : (min || 0);
        return (
          <Form.Group key={index} className="mb-3">
            <Form.Label>{label}</Form.Label>
            <div>
              <div className="d-flex justify-content-between mb-2">
                <span>{min || 0}</span>
                <span>{max || 10}</span>
              </div>
              <Form.Range
                min={min || 0}
                max={max || 10}
                value={scaleValue}
                name={`field_${index}`}
                disabled={readOnly}
                onChange={(e) => onChange?.(index, Number(e.target.value))}
              />
              {!readOnly && (
                <div className="text-center mt-2">
                  <strong>{scaleValue}</strong>
                </div>
              )}
              {readOnly && (
                <div className="text-center mt-2">
                  <strong>{t("common.selectedValue")}: {scaleValue}</strong>
                </div>
              )}
            </div>
          </Form.Group>
        );

      default:
        return (
          <Card key={index} className="mb-2">
            <Card.Body>
              <Badge variant="secondary">{type}</Badge>
              <div className="mt-1">{label}</div>
            </Card.Body>
          </Card>
        );
    }
  };

  return <div>{content.map((field, index) => renderField(field, index))}</div>;
};

export default QuestionnaireDecoder;


